import { screenIdeaNote } from "./ideaScreen";
import type { OwnerNoteKind, OwnerNoteStatus } from "./ownerNoteTier";

/**
 * W5 OWNERIN (Training and Auto-Learn Master Spec B1, B16; Agent Brains
 * section 9): the pure half of the idea pool. An owner note, typed anywhere and
 * anytime, becomes a DIRECTIVE or a testable HYPOTHESIS, and nothing in this
 * file applies either one.
 *
 *  - RULES FIRST, A MODEL ONLY FOR THE REMAINDER. The note class and the
 *    predicate are read from explicit cues. A model is asked only when the
 *    rules abstain, and then it may only pick an id out of a CLOSED list with
 *    `none` always in it (`readClosedChoice`). The model call itself lives in
 *    `@ackra/db` (`teachVerbs.ts`), because core has no gateway.
 *  - THE POOL ORDERS ATTENTION, IT NEVER COUNTS EVIDENCE. `ideaPool` returns
 *    hypotheses with a priority; no count of notes or edits is carried, so a
 *    hundred edits on one predicate are one hypothesis at one priority ("weight
 *    never adds to n"). Evidence is the learner's, from threads, after
 *    pre-registration (B4).
 *  - EDITS ARE SIGNAL ONLY, BEHIND A FLAG. An edit signal enters the pool only
 *    when the caller passes `editSignals: true` (the worker's
 *    `ACKRA_EDIT_SIGNALS`, default off). It becomes a hypothesis for the
 *    queue and never reaches a playbook (item 123 stands: playbook writes stay
 *    record-only). This widens item 123 only as far as a queue entry.
 *  - T3 NEVER POOLS. The tier reading (`readOwnerNoteTier`) is the caller's
 *    first gate; this module only ever sees notes that cleared it.
 */

/** The predicate kinds the learner's closed catalog speaks (design W2). */
export const IDEA_PREDICATE_KINDS = ["pattern_def", "fact_set", "stamp_code", "key_moment", "silence"] as const;
export type IdeaPredicateKind = (typeof IDEA_PREDICATE_KINDS)[number];

/** A closed-catalog predicate. Typed locally against the W2 design; W2 reconciles at integration. */
export interface IdeaPredicate {
  readonly kind: IdeaPredicateKind;
  readonly key: string;
  readonly value: string;
}

export interface OwnerPredicateEntry extends IdeaPredicate {
  /** Fixed words for the owner's screen. Never a slice of a note. */
  readonly label: string;
  /** Explicit cues. Exactly one entry hitting maps a note; zero or two abstain. */
  readonly fires: (text: string) => boolean;
}

const re = (source: string): ((text: string) => boolean) => {
  const rx = new RegExp(source, "iu");
  return (text) => rx.test(text);
};

/**
 * What an owner may speak about, in the learner's own pattern ids. The two
 * price patterns of stage 4 (`price_objection_hold`, `price_then_terms`) are
 * deliberately absent: a note that names a price reads T3 and never pools, so
 * an entry for them could only be reached by an over-read. TA-W5-01.
 *
 * W5 fix 1 (review B1): `fast_first_response` and `lab_grown_education` are
 * gone from this owner-facing list. The first sits on an SLA timer and the
 * second on the lab grown disclosure, both T3 items (CLAUDE.md section 3), so
 * neither may ever be an owner's hypothesis. They stay learner patterns in
 * stage 4; a typed note simply cannot map to them.
 */
export const OWNER_PREDICATE_CATALOG: readonly OwnerPredicateEntry[] = Object.freeze([
  {
    kind: "pattern_def",
    key: "photo_before_close",
    value: "present",
    label: "a photo sent before the close",
    fires: re("\\b(?:photos?|pictures?|pics?|images?)\\b"),
  },
]);

export const labelOfPredicateKey = (key: string): string => OWNER_PREDICATE_CATALOG.find((e) => e.key === key)?.label ?? key;

/** What a note can be. `none` is always allowed (a closed catalog with an exit). */
export const IDEA_NOTE_CLASSES = ["directive", "hypothesis", "none"] as const;
export type IdeaNoteClass = (typeof IDEA_NOTE_CLASSES)[number];

export interface RuleRead {
  readonly class: Exclude<IdeaNoteClass, "none">;
  readonly by: "owner_label" | "rule";
}

const DIRECTIVE_CUES = re(
  "\\b(?:always|never|from now on|make sure|every time|must|should always|do not|don't|dont|stop|please (?:always|never))\\b",
);
const HYPOTHESIS_CUES = re(
  "\\b(?:i think|i believe|i suspect|i(?:'|’)ve noticed|i have noticed|seems?|tends? to|works? better|better when|more likely|less likely|customers (?:who|that|seem|tend)|maybe|might|probably)\\b",
);

/**
 * The note's class by the owner's label, else by explicit cues. Both cue
 * families, or neither, abstain (null): precision beats recall (B13).
 */
export function classifyNoteByRules(text: string, ownerKind: OwnerNoteKind): RuleRead | null {
  if (ownerKind === "directive") return { class: "directive", by: "owner_label" };
  if (ownerKind === "hypothesis") return { class: "hypothesis", by: "owner_label" };
  const folded = text.normalize("NFKC");
  const d = DIRECTIVE_CUES(folded);
  const h = HYPOTHESIS_CUES(folded);
  if (d && !h) return { class: "directive", by: "rule" };
  if (h && !d) return { class: "hypothesis", by: "rule" };
  return null;
}

const toPredicate = (e: OwnerPredicateEntry): IdeaPredicate => ({ kind: e.kind, key: e.key, value: e.value });

/** Exactly one catalog entry hitting maps the note; none or several is null. */
export function mapNoteByKeywords(text: string): IdeaPredicate | null {
  // A note with a T3-substance hit maps to nothing, even beside a photo word (review B1).
  if (screenIdeaNote(text).held) return null;
  const folded = text.normalize("NFKC");
  const hits = OWNER_PREDICATE_CATALOG.filter((e) => e.fires(folded));
  const only = hits[0];
  return hits.length === 1 && only !== undefined ? toPredicate(only) : null;
}

export const predicateOfKey = (key: string): IdeaPredicate | null => {
  const e = OWNER_PREDICATE_CATALOG.find((x) => x.key === key);
  return e === undefined ? null : toPredicate(e);
};

export type IdeaDirection = "up" | "down";

const UP = re("\\b(?:works? better|helps?|helped|wins?|closes?|converts?|more likely|improves?|better when|builds? trust)\\b");
const DOWN = re("\\b(?:hurts?|loses?|kills?|scares?|puts? (?:people|them|customers) off|worse|less likely|turns? (?:people|them|customers) off)\\b");

/** The direction the owner claims, or null when the words do not say one way. */
export function directionOfNote(text: string): IdeaDirection | null {
  const folded = text.normalize("NFKC");
  const up = UP(folded);
  const down = DOWN(folded);
  if (up && !down) return "up";
  if (down && !up) return "down";
  return null;
}

export type ClosedChoice =
  | { readonly ok: true; readonly id: string }
  | { readonly ok: false; readonly defect: "not_json" | "extra_key" | "not_allowed" };

/**
 * A model's answer, read strictly: one bare or one fenced JSON object with
 * exactly the key `id`, whose value is one of `allowed`. Anything else is a
 * named defect and the caller retries once, then abstains.
 */
export function readClosedChoice(text: string, allowed: readonly string[]): ClosedChoice {
  const trimmed = text.trim();
  const fenced = /^```(?:json)?\s*\n([\s\S]*?)\n```$/.exec(trimmed);
  const body = fenced !== null ? (fenced[1] ?? "") : trimmed;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return { ok: false, defect: "not_json" };
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return { ok: false, defect: "not_json" };
  const keys = Object.keys(parsed);
  if (keys.length !== 1 || keys[0] !== "id") return { ok: false, defect: "extra_key" };
  const id = (parsed as Record<string, unknown>)["id"];
  if (typeof id !== "string" || !allowed.includes(id)) return { ok: false, defect: "not_allowed" };
  return { ok: true, id };
}

// ----------------------------------------------------------------- the pool

export const EDIT_SIGNAL_KINDS = ["edit", "approval", "outcome"] as const;

/**
 * Attention order: an edit the owner made outranks an approval, which outranks
 * a plain outcome. AWAITING RATIFICATION (design W5, not yet ruled by Kabir).
 */
export const EDIT_SIGNAL_PRIORITY: Readonly<Record<(typeof EDIT_SIGNAL_KINDS)[number], number>> = Object.freeze({
  edit: 3,
  approval: 2,
  outcome: 1,
});

/** The owner's own typed idea sorts above any machine-seen signal. AWAITING RATIFICATION. */
export const OWNER_NOTE_PRIORITY = 4;

/** A mapped hypothesis note, as the db loader hands it in. */
export interface PoolNote {
  readonly id: string;
  readonly predicate: IdeaPredicate;
  readonly direction: IdeaDirection | null;
  readonly recordedAt: string;
}

/** An edit, approval or outcome signal for a predicate (W4 produces them, behind ACKRA_EDIT_SIGNALS). */
export interface EditSignal {
  readonly id: string;
  readonly kind: (typeof EDIT_SIGNAL_KINDS)[number];
  readonly predicate: IdeaPredicate;
  readonly recordedAt: string;
}

/**
 * What the learner takes as an injected input. No count field exists on
 * purpose: the pool orders attention and is never evidence.
 */
export interface IdeaHypothesis {
  readonly id: string;
  readonly source: "owner_note" | "edit_signal";
  readonly sourceIds: readonly string[];
  readonly priority: number;
  readonly predicate: IdeaPredicate;
  readonly direction: IdeaDirection | null;
}

/** A pooled cell of threads for a hypothesis, as the learner computes it. */
export interface PooledCell {
  readonly withN: number;
  readonly withWon: number;
  readonly withoutN: number;
  readonly withoutWon: number;
  /** 95% interval on the won-rate gap, with minus without, in points. Null when not computed. */
  readonly wilson95Pp: readonly [number, number] | null;
}

const predicateId = (p: IdeaPredicate): string => `idea:${p.kind}:${p.key}:${p.value}`;

/** A pool input the pool refused, by id and a fixed reason. Never a slice of a note. */
export interface PoolRefusal {
  readonly id: string;
  readonly source: "owner_note" | "edit_signal";
  readonly reason: "predicate_outside_owner_catalog";
}

const inOwnerCatalog = (p: IdeaPredicate): boolean =>
  OWNER_PREDICATE_CATALOG.some((e) => e.kind === p.kind && e.key === p.key && e.value === p.value);

/**
 * The pool, with what it refused (review M6). A note or an edit signal whose
 * predicate is outside the owner catalog is refused with a reason, never passed
 * through: the catalog is the closed list of what the owner may speak about, and
 * an edit signal is no exception (it is a T3 item's predicate that would
 * otherwise reach the learner's queue).
 */
export function ideaPoolWithRefusals(
  notes: readonly PoolNote[],
  edits: readonly EditSignal[],
  opts: { readonly editSignals: boolean },
): { readonly pool: IdeaHypothesis[]; readonly refused: readonly PoolRefusal[] } {
  const refused: PoolRefusal[] = [];
  const keptNotes = notes.filter((n) => {
    if (inOwnerCatalog(n.predicate)) return true;
    refused.push({ id: n.id, source: "owner_note", reason: "predicate_outside_owner_catalog" });
    return false;
  });
  const keptEdits = edits.filter((e) => {
    if (inOwnerCatalog(e.predicate)) return true;
    if (opts.editSignals) refused.push({ id: e.id, source: "edit_signal", reason: "predicate_outside_owner_catalog" });
    return false;
  });
  return { pool: poolOf(keptNotes, keptEdits, opts), refused };
}

/** The pool as the learner takes it; what was refused is `ideaPoolWithRefusals`'s to report. */
export function ideaPool(
  notes: readonly PoolNote[],
  edits: readonly EditSignal[],
  opts: { readonly editSignals: boolean },
): IdeaHypothesis[] {
  return ideaPoolWithRefusals(notes, edits, opts).pool;
}

function poolOf(
  notes: readonly PoolNote[],
  edits: readonly EditSignal[],
  opts: { readonly editSignals: boolean },
): IdeaHypothesis[] {
  interface Acc {
    predicate: IdeaPredicate;
    noteIds: string[];
    editIds: string[];
    direction: IdeaDirection | null;
    directionAt: string;
    priority: number;
  }
  const byId = new Map<string, Acc>();
  const slot = (p: IdeaPredicate): Acc => {
    const id = predicateId(p);
    let a = byId.get(id);
    if (a === undefined) {
      a = { predicate: p, noteIds: [], editIds: [], direction: null, directionAt: "", priority: 0 };
      byId.set(id, a);
    }
    return a;
  };

  const orderedNotes = [...notes].sort((x, y) => (x.recordedAt < y.recordedAt ? -1 : x.recordedAt > y.recordedAt ? 1 : x.id < y.id ? -1 : 1));
  for (const n of orderedNotes) {
    const a = slot(n.predicate);
    a.noteIds.push(n.id);
    a.priority = Math.max(a.priority, OWNER_NOTE_PRIORITY);
    // The earliest stated direction stands; a later note that disagrees is a different claim for the owner to confirm.
    if (a.direction === null && n.direction !== null) {
      a.direction = n.direction;
      a.directionAt = n.recordedAt;
    }
  }
  if (opts.editSignals) {
    const orderedEdits = [...edits].sort((x, y) => (x.id < y.id ? -1 : x.id > y.id ? 1 : 0));
    for (const e of orderedEdits) {
      const a = slot(e.predicate);
      a.editIds.push(e.id);
      a.priority = Math.max(a.priority, EDIT_SIGNAL_PRIORITY[e.kind]);
    }
  }

  const out: IdeaHypothesis[] = [...byId.entries()].map(([id, a]) => ({
    id,
    source: a.noteIds.length > 0 ? ("owner_note" as const) : ("edit_signal" as const),
    sourceIds: [...a.noteIds, ...a.editIds],
    priority: a.priority,
    predicate: a.predicate,
    direction: a.noteIds.length > 0 ? a.direction : null,
  }));
  return out.sort((x, y) => y.priority - x.priority || (x.id < y.id ? -1 : x.id > y.id ? 1 : 0));
}

// ------------------------------------------------------ the confirm digest

export interface ConfirmRow {
  readonly noteId: string;
  readonly predicateKey: string | null;
  readonly status: OwnerNoteStatus;
}

/** Statuses that wait on the owner's word: the digest asks about these and nothing else. */
const WAITING: readonly OwnerNoteStatus[] = ["in_pool", "mapped", "tested"];

/**
 * The weekly digest's confirm or correct section, as lines. A template: the
 * fixed catalog label and the id, never a word of the owner's note. Template
 * addition only; W1's digest builder places it.
 */
export function renderConfirmCorrectSection(rows: readonly ConfirmRow[]): string[] {
  const waiting = rows.filter((r) => WAITING.includes(r.status));
  if (waiting.length === 0) return ["Confirm or correct: nothing is waiting on you."];
  const lines = [`Confirm or correct: ${waiting.length} idea${waiting.length === 1 ? "" : "s"} waiting on you.`];
  for (const r of waiting) {
    const about = r.predicateKey === null ? "not yet something I can count" : labelOfPredicateKey(r.predicateKey);
    lines.push(`  ${r.noteId}  ${about}  (${r.status})`);
  }
  lines.push('  Reply with: pnpm teach confirm <id>   or   pnpm teach correct <id> "<why>"');
  return lines;
}
